import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../../../hooks/useSubscription';
import { PublicationType } from '../../../hooks/usePublications';
import { useTenant } from '../../../contexts/TenantContext';
import { useCustomer } from '../../../contexts/CustomerContext';
import PushNotificationToggle from '../../../components/PushNotificationToggle';
import './SubscriptionsPage.css';
import { logger } from '../../../utils/logger';

interface SubscriptionsPageProps {
  tenantId?: string;
  customerId?: string;
  merchantName?: string;
}

const SubscriptionsPage: React.FC<SubscriptionsPageProps> = ({
  tenantId: propTenantId,
  customerId: propCustomerId,
  merchantName: propMerchantName,
}) => {
  const navigate = useNavigate();
  const { tenant } = useTenant();
  const { customer, isAuthenticated } = useCustomer();

  const tenantId = propTenantId || tenant?.id || '';
  const customerId = propCustomerId || customer?.id;
  const merchantName = propMerchantName || tenant?.name || 'ce marchand';

  const {
    subscription,
    loading,
    isSubscribed,
    subscribe,
    unsubscribe,
    updatePreferences,
  } = useSubscription(tenantId, customerId);

  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const publicationTypes: { value: PublicationType; label: string; icon: string; description: string }[] = [
    {
      value: 'ARRIVAGE',
      label: 'Arrivages',
      icon: '🍅',
      description: 'Nouveaux produits frais et arrivages du jour',
    },
    {
      value: 'PROMOTION',
      label: 'Promotions',
      icon: '🎉',
      description: 'Offres spéciales et réductions',
    },
    {
      value: 'ANTI_GASPI',
      label: 'Anti-gaspi',
      icon: '🌱',
      description: 'Produits à prix réduit pour éviter le gaspillage',
    },
    {
      value: 'EVENEMENT',
      label: 'Événements',
      icon: '🎪',
      description: 'Dégustations, visites de producteurs...',
    },
    {
      value: 'INFO',
      label: 'Informations',
      icon: 'ℹ️',
      description: 'Actualités et informations générales',
    },
  ];

  const handleSubscribe = async () => {
    if (!customerId) {
      setMessage({
        type: 'error',
        text: 'Vous devez être connecté pour vous abonner',
      });
      return;
    }

    setSaving(true);
    setMessage(null);

    try {
      await subscribe({
        push_enabled: true,
        email_enabled: false,
        sms_enabled: false,
        preferred_types: publicationTypes.map((t) => t.value),
      });

      setMessage({
        type: 'success',
        text: `Vous êtes maintenant abonné à ${merchantName} !`,
      });
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.message || 'Erreur lors de l\'abonnement',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleUnsubscribe = async () => {
    // logger.log('handleUnsubscribe called');
    if (!confirm('Êtes-vous sûr de vouloir vous désabonner ?')) {
      // logger.log('User cancelled unsubscribe');
      return;
    }

    setSaving(true);
    setMessage(null);

    try {
      // logger.log('Calling unsubscribe API...');
      await unsubscribe();

      setMessage({
        type: 'success',
        text: 'Vous avez été désabonné avec succès',
      });
    } catch (error: any) {
      logger.error('Error unsubscribing:', error);
      setMessage({
        type: 'error',
        text: error.message || 'Erreur lors du désabonnement',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleChannel = async (channel: 'push' | 'email' | 'sms') => {
    // logger.log('handleToggleChannel called with:', channel);
    if (!subscription) {
      // logger.log('No subscription, aborting');
      return;
    }

    // logger.log('Current subscription:', subscription);
    setSaving(true);
    setMessage(null);

    try {
      const newPreferences = {
        push_enabled: channel === 'push' ? !subscription.push_enabled : subscription.push_enabled,
        email_enabled: channel === 'email' ? !subscription.email_enabled : subscription.email_enabled,
        sms_enabled: channel === 'sms' ? !subscription.sms_enabled : subscription.sms_enabled,
      };
      logger.log('Updating with:', newPreferences);

      await updatePreferences(newPreferences);

      setMessage({
        type: 'success',
        text: 'Préférences mises à jour',
      });
    } catch (error: any) {
      logger.error('Error updating channel:', error);
      setMessage({
        type: 'error',
        text: error.message || 'Erreur lors de la mise à jour',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleType = async (type: PublicationType) => {
    // logger.log('handleToggleType called with:', type);
    if (!subscription) {
      // logger.log('No subscription, aborting');
      return;
    }

    const currentTypes = subscription.preferences?.types || [];
    const newTypes = currentTypes.includes(type)
      ? currentTypes.filter((t) => t !== type)
      : [...currentTypes, type];

    // logger.log('Current types:', currentTypes, 'New types:', newTypes);

    setSaving(true);
    setMessage(null);

    try {
      await updatePreferences({
        preferred_types: newTypes,
      });

      setMessage({
        type: 'success',
        text: 'Préférences mises à jour',
      });
    } catch (error: any) {
      logger.error('Error updating type:', error);
      setMessage({
        type: 'error',
        text: error.message || 'Erreur lors de la mise à jour',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="subscriptions-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Chargement...</p>
        </div>
      </div>
    );
  }

  // Debug logs removed to avoid console pollution

  return (
    <div className="subscriptions-page">
      <div className="page-header">
        <button
          onClick={() => navigate('/shop')}
          className="btn-back"
          style={{
            marginBottom: '1.5rem',
            padding: '0.75rem 1.5rem',
            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: '1rem',
            boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)',
            transition: 'all 0.3s',
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.4)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(59, 130, 246, 0.3)';
          }}
        >
          ← Retour à la boutique
        </button>
        <h1>Notifications</h1>
        <p className="page-subtitle">
          Recevez les actualités de <strong>{merchantName}</strong>
        </p>
        {!customerId && (
          <div className="warning-message">
            ⚠️ Vous devez être connecté pour gérer vos abonnements
          </div>
        )}
      </div>

      {message && (
        <div className={`message message-${message.type}`}>
          {message.type === 'success' ? '✅' : '⚠️'} {message.text}
        </div>
      )}

      {!isAuthenticated ? (
        <div className="not-subscribed-card">
          <div className="icon-large">🔐</div>
          <h2>Connexion requise</h2>
          <p>
            Vous devez être connecté pour gérer vos abonnements aux notifications.
          </p>
          <button
            onClick={() => navigate('/shop/login')}
            className="btn-subscribe-large"
          >
            🔐 Se connecter
          </button>
        </div>
      ) : !isSubscribed ? (
        <div className="not-subscribed-card">
          <div className="icon-large">🔔</div>
          <h2>Restez informé !</h2>
          <p>
            Recevez une notification à chaque nouvelle publication :{' '}
            arrivages frais, promotions exclusives, événements...
          </p>
          <button
            onClick={handleSubscribe}
            disabled={saving}
            className="btn-subscribe-large"
          >
            {saving ? 'Abonnement...' : '🔔 S\'abonner aux notifications'}
          </button>
        </div>
      ) : (
        <>
          {/* Canaux de notification */}
          <div className="settings-section">
            <h2 className="section-title">Canaux de notification</h2>

            <PushNotificationToggle
              tenantId={tenantId}
              customerId={customerId}
              isSubscribed={isSubscribed}
              onSuccess={() => {
                setMessage({
                  type: 'success',
                  text: 'Notifications push configurées avec succès',
                });
              }}
              onError={(error) => {
                setMessage({
                  type: 'error',
                  text: error,
                });
              }}
            />

            <div className="setting-item">
              <div className="setting-info">
                <div className="setting-icon">📧</div>
                <div>
                  <div className="setting-label">Email</div>
                  <div className="setting-description">
                    Recevez un résumé quotidien par email
                  </div>
                </div>
              </div>
              <label className="toggle">
                <input
                  type="checkbox"
                  checked={subscription?.email_enabled || false}
                  onChange={() => handleToggleChannel('email')}
                  disabled={saving}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <div className="setting-icon">💬</div>
                <div>
                  <div className="setting-label">SMS</div>
                  <div className="setting-description">
                    Notifications urgentes par SMS (anti-gaspi...)
                  </div>
                </div>
              </div>
              <label className="toggle">
                <input
                  type="checkbox"
                  checked={subscription?.sms_enabled || false}
                  onChange={() => handleToggleChannel('sms')}
                  disabled={saving}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
          </div>

          {/* Types de publications */}
          <div className="settings-section">
            <h2 className="section-title">Types de publications</h2>
            <p className="section-description">
              Choisissez les types de publications que vous souhaitez recevoir
            </p>

            <div className="types-grid">
              {publicationTypes.map((type) => {
                const isSelected = subscription?.preferences?.types?.includes(type.value) ?? true;

                return (
                  <div
                    key={type.value}
                    className={`type-card ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleToggleType(type.value)}
                  >
                    <div className="type-icon">{type.icon}</div>
                    <div className="type-label">{type.label}</div>
                    <div className="type-description">{type.description}</div>
                    {isSelected && <div className="checkmark">✓</div>}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Se désabonner */}
          <div className="danger-zone">
            <h3>Se désabonner</h3>
            <p>Vous ne recevrez plus aucune notification de ce marchand</p>
            <button
              onClick={handleUnsubscribe}
              disabled={saving}
              className="btn-unsubscribe"
            >
              Se désabonner
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default SubscriptionsPage;
